import type { ServerMessage } from 'ts-components/PiChat/protocol';
import type { PiChatFrameMeta } from 'ts-components/PiChat/usePiChat';
import {
  handleCaptureRequest,
  type CaptureContext,
} from 'ts-components/PiChat/capture/captureRequest';
import type { UiCaptureMessage } from './captureTarget';

export interface KiCodeCaptureDeps {
  capture(message: UiCaptureMessage, context: CaptureContext): void;
}

export interface KiCodeCapture {
  handleServerMessage(message: ServerMessage, meta: PiChatFrameMeta): void;
  dispose(): void;
}

export const createKiCodeCapture = ({
  capture,
}: KiCodeCaptureDeps): KiCodeCapture => {
  const closed = new AbortController();
  const inFlight = new WeakSet<AbortSignal>();
  const signals = new WeakMap<AbortSignal, AbortSignal>();
  const sessionSignal = (socket: AbortSignal) => {
    let signal = signals.get(socket);
    if (!signal) {
      const controller = new AbortController();
      const abort = () => controller.abort();
      socket.addEventListener('abort', abort);
      closed.signal.addEventListener('abort', abort);
      signal = controller.signal;
      signals.set(socket, signal);
    }
    return signal;
  };

  return {
    handleServerMessage(message, meta) {
      if (message.type !== 'ui.capture' || closed.signal.aborted) return;
      handleCaptureRequest(
        message,
        { ...meta, signal: sessionSignal(meta.signal) },
        capture,
        inFlight
      );
    },
    dispose() {
      closed.abort();
    },
  };
};
